/**
 * Weekly selection — up to 4 curated opposite-sex suggestions per ISO week.
 * Storage: un8.matching.weekly.v1
 *
 * Pass replacement: within 24h of a pass, fill a new open slot from the
 * remaining eligible pool when possible. Never pad with poor fits.
 */

import { writeLocal } from "../storage/localWrite";
import { mixDemoTiers } from "./chromePlan";
import { buildDailyDeck } from "./engine";
import type { MatchProfile } from "./types";
import { oppositeGender } from "./types";
import { WEEKLY_SUGGESTION_CAP, PASS_REPLACE_MS } from "./weights";

export const WEEKLY_STORAGE_KEY = "un8.matching.weekly.v1";

export type WeeklySlotStatus = "open" | "liked" | "passed" | "replaced";

export type WeeklySlot = {
  profileId: string;
  status: WeeklySlotStatus;
  presentedAt: string;
  actedAt?: string;
  /** ISO deadline — pass may be replaced until this time when pool allows. */
  replaceUntil?: string;
};

export type WeeklyRecord = {
  userId: string;
  weekKey: string;
  slots: WeeklySlot[];
  /** How many we aimed for this week (usually 4; lower when pool is thin). */
  targetCount: number;
  thinPool: boolean;
  /** Subscriber-only spendable reveals beyond the weekly four. */
  extraRevealsRemaining: number;
};

type Listener = () => void;
const listeners = new Set<Listener>();

function notify() {
  for (const fn of listeners) {
    try {
      fn();
    } catch {
      /* ignore */
    }
  }
}

/** ISO week key: YYYY-Www (UTC Monday weeks). */
export function weekKey(d: Date = new Date()): string {
  const date = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const dayNum = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(((date.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7);
  return `${date.getUTCFullYear()}-W${String(weekNo).padStart(2, "0")}`;
}

/** Next Monday 00:00 UTC after the current ISO week. */
export function nextWeekStart(d: Date = new Date()): Date {
  const date = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const dayNum = date.getUTCDay() || 7; // Mon=1 … Sun=7
  const daysUntilMonday = dayNum === 1 ? 7 : 8 - dayNum;
  date.setUTCDate(date.getUTCDate() + daysUntilMonday);
  date.setUTCHours(0, 0, 0, 0);
  return date;
}

export function daysUntilNextSelection(d: Date = new Date()): number {
  const ms = nextWeekStart(d).getTime() - d.getTime();
  return Math.max(0, Math.ceil(ms / 86_400_000));
}

function normalizeRecord(raw: unknown): WeeklyRecord | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as WeeklyRecord & { shortWeek?: boolean };
  const thinPool =
    typeof r.thinPool === "boolean"
      ? r.thinPool
      : typeof r.shortWeek === "boolean"
        ? r.shortWeek
        : false;
  return { ...r, thinPool };
}

function readAll(): Record<string, WeeklyRecord> {
  try {
    const raw = localStorage.getItem(WEEKLY_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const out: Record<string, WeeklyRecord> = {};
    for (const [id, value] of Object.entries(parsed)) {
      const rec = normalizeRecord(value);
      if (rec) out[id] = rec;
    }
    return out;
  } catch {
    return {};
  }
}

function writeAll(map: Record<string, WeeklyRecord>) {
  const next = JSON.stringify(map);
  let prev: string | null = null;
  try {
    prev = localStorage.getItem(WEEKLY_STORAGE_KEY);
  } catch {
    prev = null;
  }
  if (prev === next) return;
  writeLocal(WEEKLY_STORAGE_KEY, next);
  notify();
}

function save(rec: WeeklyRecord) {
  const map = readAll();
  map[rec.userId] = rec;
  writeAll(map);
}

function slotIds(slots: WeeklySlot[], statuses?: WeeklySlotStatus[]): Set<string> {
  const set = new Set<string>();
  for (const s of slots) {
    if (statuses && !statuses.includes(s.status)) continue;
    set.add(s.profileId);
  }
  return set;
}

function poolHasDemoCandidates(pool: MatchProfile[]): boolean {
  return pool.some((p) => p.source === "demo" || p.id.startsWith("cand_"));
}

function hasOppositeSexCandidates(
  self: MatchProfile,
  pool: MatchProfile[],
  excluded: ReadonlySet<string>
): boolean {
  const want = oppositeGender(self.gender);
  return pool.some(
    (p) => p.id !== self.id && p.gender === want && !excluded.has(p.id)
  );
}

function pickIds(
  self: MatchProfile,
  pool: MatchProfile[],
  excluded: ReadonlySet<string>,
  limit: number,
  preferIds?: ReadonlySet<string>
): string[] {
  if (limit <= 0) return [];
  const deck = buildDailyDeck({
    self,
    pool,
    excludedIds: excluded,
    limit,
    preferIds,
  });
  if (deck.candidates.length > 0) {
    return applyDemoTierMix(
      deck.candidates.map((c) => c.profile.id),
      self,
      pool,
      excluded
    );
  }
  // Hard gates can remove every demo candidate for strict real profiles
  // (e.g. requiredPartnerFaith 95 vs demo max 90). Closed-test demo pool
  // must still open on approval day — fall back to values-score ranking.
  if (!poolHasDemoCandidates(pool)) return [];
  if (!hasOppositeSexCandidates(self, pool, excluded)) return [];
  const relaxed = buildDailyDeck({
    self,
    pool,
    excludedIds: excluded,
    limit,
    skipGates: true,
    preferIds,
  });
  return applyDemoTierMix(
    relaxed.candidates.map((c) => c.profile.id),
    self,
    pool,
    excluded
  );
}

function applyDemoTierMix(
  ids: string[],
  self: MatchProfile,
  pool: MatchProfile[],
  excluded: ReadonlySet<string>
): string[] {
  if (ids.length === 0 || !poolHasDemoCandidates(pool)) return ids;
  const byId = new Map(pool.map((p) => [p.id, p]));
  const selected = ids
    .map((id) => byId.get(id))
    .filter((p): p is MatchProfile => Boolean(p));
  if (selected.length === 0) return ids;
  const want = oppositeGender(self.gender);
  const eligible = pool.filter(
    (p) => p.id !== self.id && p.gender === want && !excluded.has(p.id)
  );
  return mixDemoTiers(selected, eligible).map((p) => p.id);
}

/**
 * Ensure this week's selection exists. Rebuilds on new weekKey.
 * Immediately fills pass-replacements that are still inside the 24h window.
 */
export function ensureWeeklySelection(input: {
  self: MatchProfile;
  pool: MatchProfile[];
  /** Permanently excluded: liked, matched, historically passed (except replaceable). */
  excludedIds: ReadonlySet<string>;
  /** Subscriber: seed 2 extra reveals each new week. */
  isSubscriber: boolean;
  /** People who already liked you — soft preference in ranking. */
  preferIds?: ReadonlySet<string>;
  now?: Date;
}): WeeklyRecord {
  const now = input.now ?? new Date();
  const key = weekKey(now);
  const map = readAll();
  let rec = map[input.self.id];
  const prefer = input.preferIds;

  // Never keep the viewer in their own weekly slots (corrupt / legacy rows).
  if (rec?.slots.some((s) => s.profileId === input.self.id)) {
    const cleaned = rec.slots.filter((s) => s.profileId !== input.self.id);
    const need = Math.max(
      0,
      WEEKLY_SUGGESTION_CAP - cleaned.filter((s) => s.status !== "replaced").length
    );
    const exclude = new Set(input.excludedIds);
    for (const id of slotIds(cleaned)) exclude.add(id);
    const fill = pickIds(input.self, input.pool, exclude, need, prefer);
    const presentedAt = now.toISOString();
    const nextSlots: WeeklySlot[] = [
      ...cleaned,
      ...fill.map((profileId) => ({
        profileId,
        status: "open" as const,
        presentedAt,
      })),
    ];
    rec = {
      ...rec,
      slots: nextSlots,
      thinPool:
        nextSlots.filter((s) => s.status !== "replaced").length < WEEKLY_SUGGESTION_CAP,
    };
    save(rec);
  }

  if (!rec || rec.weekKey !== key || rec.userId !== input.self.id) {
    const ids = pickIds(
      input.self,
      input.pool,
      input.excludedIds,
      WEEKLY_SUGGESTION_CAP,
      prefer
    );
    const presentedAt = now.toISOString();
    rec = {
      userId: input.self.id,
      weekKey: key,
      slots: ids.map((profileId) => ({
        profileId,
        status: "open" as const,
        presentedAt,
      })),
      targetCount: WEEKLY_SUGGESTION_CAP,
      thinPool: ids.length < WEEKLY_SUGGESTION_CAP,
      extraRevealsRemaining: input.isSubscriber ? 2 : 0,
    };
    save(rec);
    return tryReplacePasses({ ...input, rec, now });
  }

  // Same week, but under-filled (empty pool earlier, or all slots were self).
  // Refill from the current pool so approved members / guests get cards when
  // demos land after the week row was first written.
  {
    const active = rec.slots.filter(
      (s) => s.status !== "replaced" && s.profileId !== input.self.id
    );
    const need = Math.max(0, WEEKLY_SUGGESTION_CAP - active.length);
    if (need > 0) {
      const exclude = new Set(input.excludedIds);
      for (const id of slotIds(rec.slots)) exclude.add(id);
      exclude.add(input.self.id);
      const fill = pickIds(input.self, input.pool, exclude, need, prefer);
      if (fill.length > 0) {
        const presentedAt = now.toISOString();
        const kept = rec.slots.filter((s) => s.profileId !== input.self.id);
        const nextSlots: WeeklySlot[] = [
          ...kept,
          ...fill.map((profileId) => ({
            profileId,
            status: "open" as const,
            presentedAt,
          })),
        ];
        rec = {
          ...rec,
          slots: nextSlots,
          thinPool:
            nextSlots.filter((s) => s.status !== "replaced").length <
            WEEKLY_SUGGESTION_CAP,
        };
        save(rec);
      }
    }
  }

  // Unused demo week: swap a duplicate so bronze / silver / gold all show.
  if (
    rec &&
    poolHasDemoCandidates(input.pool) &&
    rec.slots.length > 0 &&
    rec.slots.every((s) => s.status === "open")
  ) {
    const mixed = applyDemoTierMix(
      rec.slots.map((s) => s.profileId),
      input.self,
      input.pool,
      input.excludedIds
    );
    const same =
      mixed.length === rec.slots.length &&
      mixed.every((id, i) => id === rec.slots[i].profileId);
    if (!same) {
      rec = {
        ...rec,
        slots: rec.slots.map((s, i) => ({
          ...s,
          profileId: mixed[i] ?? s.profileId,
        })),
      };
      save(rec);
    }
  }

  return tryReplacePasses({ ...input, rec, now });
}

function tryReplacePasses(input: {
  self: MatchProfile;
  pool: MatchProfile[];
  excludedIds: ReadonlySet<string>;
  preferIds?: ReadonlySet<string>;
  rec: WeeklyRecord;
  now: Date;
}): WeeklyRecord {
  const { rec, now } = input;
  let changed = false;
  const nextSlots = [...rec.slots];

  for (let i = 0; i < nextSlots.length; i++) {
    const slot = nextSlots[i];
    if (slot.status !== "passed" || !slot.replaceUntil) continue;
    if (Date.parse(slot.replaceUntil) < now.getTime()) continue;

    const exclude = new Set(input.excludedIds);
    // Anti-waste: never re-present anyone already shown this week (open,
    // liked, passed, or replaced-away). Passes stay excluded via interestStore
    // across weeks; this blocks same-week recycle if replace raced exclusion.
    for (const id of slotIds(nextSlots)) exclude.add(id);

    const [replacement] = pickIds(
      input.self,
      input.pool,
      exclude,
      1,
      input.preferIds
    );
    if (!replacement) continue;

    nextSlots[i] = { ...slot, status: "replaced", actedAt: slot.actedAt ?? now.toISOString() };
    nextSlots.push({
      profileId: replacement,
      status: "open",
      presentedAt: now.toISOString(),
    });
    changed = true;
  }

  if (!changed) return rec;
  const updated: WeeklyRecord = { ...rec, slots: nextSlots };
  save(updated);
  return updated;
}

export function markWeeklyLiked(userId: string, profileId: string, now = new Date()): WeeklyRecord | null {
  const map = readAll();
  const rec = map[userId];
  if (!rec) return null;
  const slots = rec.slots.map((s) =>
    s.profileId === profileId && s.status === "open"
      ? { ...s, status: "liked" as const, actedAt: now.toISOString() }
      : s
  );
  const updated = { ...rec, slots };
  save(updated);
  return updated;
}

export function markWeeklyPassed(userId: string, profileId: string, now = new Date()): WeeklyRecord | null {
  const map = readAll();
  const rec = map[userId];
  if (!rec) return null;
  const actedAt = now.toISOString();
  const replaceUntil = new Date(now.getTime() + PASS_REPLACE_MS).toISOString();
  const slots = rec.slots.map((s) =>
    s.profileId === profileId && s.status === "open"
      ? { ...s, status: "passed" as const, actedAt, replaceUntil }
      : s
  );
  const updated = { ...rec, slots };
  save(updated);
  return updated;
}

/** Spend one paid extra reveal — adds an open slot if pool allows. */
export function spendExtraReveal(input: {
  self: MatchProfile;
  pool: MatchProfile[];
  excludedIds: ReadonlySet<string>;
  preferIds?: ReadonlySet<string>;
  now?: Date;
}): { ok: true; record: WeeklyRecord } | { ok: false; error: "none_left" | "no_pool" | "no_week" } {
  const now = input.now ?? new Date();
  const map = readAll();
  const rec = map[input.self.id];
  if (!rec || rec.weekKey !== weekKey(now)) return { ok: false, error: "no_week" };
  if (rec.extraRevealsRemaining <= 0) return { ok: false, error: "none_left" };

  const exclude = new Set(input.excludedIds);
  for (const id of slotIds(rec.slots)) exclude.add(id);
  const [id] = pickIds(input.self, input.pool, exclude, 1, input.preferIds);
  if (!id) return { ok: false, error: "no_pool" };

  const updated: WeeklyRecord = {
    ...rec,
    extraRevealsRemaining: rec.extraRevealsRemaining - 1,
    slots: [
      ...rec.slots,
      { profileId: id, status: "open", presentedAt: now.toISOString() },
    ],
  };
  save(updated);
  return { ok: true, record: updated };
}

export function getWeeklyRecord(userId: string | null): WeeklyRecord | null {
  if (!userId) return null;
  return readAll()[userId] ?? null;
}

/** Open (still undecided) profile ids for the current week. */
export function openWeeklyIds(rec: WeeklyRecord | null): string[] {
  if (!rec) return [];
  return rec.slots.filter((s) => s.status === "open").map((s) => s.profileId);
}

/** Cards still on the weekly grid — open plus already-liked. Passes drop. */
export function visibleWeeklyIds(rec: WeeklyRecord | null): string[] {
  if (!rec) return [];
  return rec.slots
    .filter((s) => s.status === "open" || s.status === "liked")
    .map((s) => s.profileId);
}

/** All presented ids this week (for syncing session.deckIds). */
export function presentedWeeklyIds(rec: WeeklyRecord | null): string[] {
  if (!rec) return [];
  return rec.slots
    .filter((s) => s.status !== "replaced")
    .map((s) => s.profileId);
}

export function clearWeeklyForUser(userId: string) {
  const map = readAll();
  delete map[userId];
  writeAll(map);
}

/**
 * Rebuild this week's four (after credit / entitlement check upstream).
 * Prior open/liked/passed ids stay excluded so the swap is genuinely new.
 */
export function refreshWeeklySelection(input: {
  self: MatchProfile;
  pool: MatchProfile[];
  excludedIds: ReadonlySet<string>;
  isSubscriber: boolean;
  preferIds?: ReadonlySet<string>;
  now?: Date;
}): WeeklyRecord {
  const now = input.now ?? new Date();
  const key = weekKey(now);
  const exclude = new Set(input.excludedIds);
  const existing = readAll()[input.self.id];
  if (existing?.weekKey === key) {
    for (const id of slotIds(existing.slots)) exclude.add(id);
  }
  exclude.add(input.self.id);
  const ids = pickIds(
    input.self,
    input.pool,
    exclude,
    WEEKLY_SUGGESTION_CAP,
    input.preferIds
  );
  const presentedAt = now.toISOString();
  const rec: WeeklyRecord = {
    userId: input.self.id,
    weekKey: key,
    slots: ids.map((profileId) => ({
      profileId,
      status: "open" as const,
      presentedAt,
    })),
    targetCount: WEEKLY_SUGGESTION_CAP,
    thinPool: ids.length < WEEKLY_SUGGESTION_CAP,
    extraRevealsRemaining: input.isSubscriber
      ? existing?.extraRevealsRemaining ?? 2
      : 0,
  };
  save(rec);
  return rec;
}

export const weeklyStore = {
  subscribe(fn: Listener): () => void {
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  },
  weekKey,
  nextWeekStart,
  daysUntilNextSelection,
  ensureWeeklySelection,
  refreshWeeklySelection,
  markWeeklyLiked,
  markWeeklyPassed,
  spendExtraReveal,
  getWeeklyRecord,
  openWeeklyIds,
  visibleWeeklyIds,
  presentedWeeklyIds,
  clearWeeklyForUser,
  STORAGE_KEY: WEEKLY_STORAGE_KEY,
};
